import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { IconButtonTemplate } from '../../buttons/IconButtonTemplate';
import { Checkbox } from '../../shadcn/checkbox';
import Map from '../../mapComponent/Map';
import { MapInfoButton } from '../../mapComponent/MapInfoButton';
import { Dialog } from '../../Dialog';

import {
  realtyButtonsTitles,
  realtyLocationButtons,
} from '../../../../data/cardRealty';
import {
  colors,
  errorMessages,
  successMessages,
} from '../../../../data/constants';
import { handleCopy, isValidCoords } from '../../../../utils/utils';
import { showError } from '../../../../utils/ui';
import { useIsMobile } from '../../../../hooks/useMediaQuery';
import { selectUserToken } from '../../../../redux/features/user/userSelectors';
import { selectSearchFilters } from '../../../../redux/features/dashboard/dashboardSelectors';
import { useGetSearchRealtiesMapQuery } from '../../../../redux/services/search/searchApi';

export const LocationButtons = ({
  realtyId,
  realtyLocation = {},
  updateRealtyData = () => {},
  canEdit = false,
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const userToken = useSelector(selectUserToken);
  const searchFilters = useSelector(selectSearchFilters);
  const {
    location_address_checked,
    location_city_id,
    location_street_id,
    location_street_number_id,
    latitude,
    longitude,
  } = realtyLocation ?? {};
  let streetNumber = location_street_number_id?.name
    ? `${location_street_number_id.name}, `
    : '';
  const address = location_street_id?.name
    ? `${streetNumber}${location_street_id.name}`
    : location_city_id?.name ?? '';

  const [isAddressChecked, setIsAddressChecked] = useState(
    location_address_checked
  );
  const [isMapShown, setIsMapShown] = useState(false);

  const { data: realtiesMap = [], isFetching: isRealtiesMapFetching } =
    useGetSearchRealtiesMapQuery(
      {
        token: userToken,
        search_filters: searchFilters ?? '',
      },
      { skip: !isMapShown }
    );

  const handleCheckClick = inputName => {
    const value = isAddressChecked ? 0 : 1;

    updateRealtyData({ [inputName]: value });

    setIsAddressChecked(!isAddressChecked);
  };

  const handleCoordsClick = () => {
    const streetName = location_street_id?.name;
    const hasCoords = longitude && latitude;
    let url = `${latitude},${longitude}`;

    if (!hasCoords && streetName) {
      url = `${streetNumber}${location_street_id.name}`;
    } else if (!hasCoords && !streetName) {
      url = location_city_id?.name;
    }

    window.open(
      `https://google.com/maps/search/?api=1&map_action=map&basemap=satellite&query=${url}`
    );
  };

  const handleCopyAdressClick = () => {
    handleCopy(address, successMessages.copyAddress);
  };

  const handleMapClick = () => {
    if (!longitude || !latitude) {
      showError(errorMessages.noCoords);
      return;
    }
    if (!isValidCoords(latitude, longitude)) {
      showError(errorMessages.wrongCoords);
      return;
    }
    if (isMobile) {
      navigate(`/map?highlight=${realtyId}`);
      return;
    }
    setIsMapShown(true);
  };

  const buttonsHandlers = {
    [realtyButtonsTitles.copyAddress]: handleCopyAdressClick,
    [realtyButtonsTitles.map]: handleMapClick,
  };

  return (
    <div className="flex gap-2 justify-start items-center">
      <div
        className="flex items-center bg-whiteColor rounded w-max-content"
        onClick={
          canEdit
            ? () => handleCheckClick('location_address_checked')
            : undefined
        }
      >
        <Checkbox
          id={realtyId}
          defaultChecked={isAddressChecked}
          disabled={!canEdit}
        />
      </div>

      {location_city_id?.name ? (
        <>
          <div className="flex items-center p-0.5 bg-gray-200 rounded w-max-content">
            <span className="text-sm text-blackColor">
              {location_city_id.name}
            </span>
          </div>

          <div
            onClick={handleCoordsClick}
            className="cursor-pointer"
          >
            <span className="text-sm text-darkBlueColor underline">
              {address.length > 24 ? `${address.slice(0, 24)}...` : address}
            </span>
          </div>

          {realtyLocationButtons.map(button => {
            const isMapButton = button.title === realtyButtonsTitles.map;
            const hasCoords = Boolean(longitude && latitude);
            return (
              <IconButtonTemplate
                key={button.id}
                handleClick={buttonsHandlers[button.title]}
                icon={button.icon}
                classes={button.classes}
                isSmallBorder={true}
                size={16}
                tooltipText={button.tooltipText}
                color={isMapButton && hasCoords ? colors.greenColor : undefined}
              />
            );
          })}
        </>
      ) : (
        <span className="text-sm text-gray-400">N/A</span>
      )}

      {isMapShown && (
        <Dialog
          content={
            <div className="relative w-[254px] h-96 sm:w-[768px] sm:h-[768px]">
              <Map
                realties={realtiesMap}
                highlightId={realtyId}
                isLoading={isRealtiesMapFetching}
              />
              <MapInfoButton className="absolute top-2 right-2 opacity-80 z-[2]" />
            </div>
          }
          classes={'max-w-[290px] sm:max-w-[792px]'}
          isOpen={isMapShown}
          onClose={() => setIsMapShown(false)}
        />
      )}
    </div>
  );
};
